import { useState, useLayoutEffect, useEffect, useRef } from 'react'
import helpSVG from '../../assets/help.svg'
import closeSVG from '../../assets/close.svg'
import styled from 'styled-components'

interface TooltipProps {
    isOpen: boolean
    offsetX: number
    offsetY: number
    position: 'top' | 'bottom' | 'left' | 'right'
    textAlign?: 'left' | 'center' | 'right'
    width: number
    extraOffset?: number
}

const TooltipContainer = styled.div`
    position: relative;
    display: inline-block;
`

const TooltipBox = styled.div<TooltipProps>`
    position: absolute;
    ${({ position, offsetX, offsetY, extraOffset = 0 }) => {
        switch (position) {
            case 'bottom':
                return `top: 100%; left: 50%; transform: translateX(-50%) translateX(${offsetX}px); margin-top: ${
                    8 + extraOffset
                }px;`
            case 'left':
                return `right: 100%; top: 50%; transform: translateY(-50%) translateY(${offsetY}px); margin-right: ${
                    8 + extraOffset
                }px;`
            case 'right':
                return `left: 100%; top: 50%; transform: translateY(-50%) translateY(${offsetY}px); margin-left: ${
                    8 + extraOffset
                }px;`
            default:
                // 'top'
                return `bottom: 100%; left: 50%; transform: translateX(-50%) translateX(${offsetX}px); margin-bottom: ${
                    8 + extraOffset
                }px;`
        }
    }}
    width: ${({ width }) => `${width}px`};
    background: #252525;
    padding: 16px;
    border-radius: 8px;
    font-size: 13px;
    letter-spacing: -0.028em;
    white-space: normal;
    z-index: 100;
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
    transition: opacity 0.2s ease-in-out;

  > p {
    color: white;
  }

    ul {
        list-style: none;
        padding: 0;
        margin: 0;

        > li {
            position: relative;
            margin-left: 10px;
            padding-left: 10px;
            font-size: 12.5px;
            line-height: 18.2px;
            color: #b1b1b1;
            text-align: ${({ textAlign }) => textAlign || 'center'};

            &::before {
                content: '';
                position: absolute;
                left: 0;
                top: 50%;
                transform: translateY(-50%);
                width: 3px;
                height: 3px;
                background: grey;
                border-radius: 50%;
            }
        }
    }
  }
`

const CloseButton = styled.button`
    background: transparent;
    border: none;
    color: white;
    font-size: 14px;
    margin-left: auto;
    cursor: pointer;
`

interface TooltipComponentProps {
    label?: string
    tooltipText?: React.ReactNode
    isSimple?: boolean
    tooltipPosition?: 'top' | 'bottom' | 'left' | 'right'
    textAlign?: 'left' | 'center' | 'right'
    width?: number
    extraOffset?: number
}

export default function TooltipComponent({
    label,
    tooltipText,
    isSimple,
    tooltipPosition = 'top',
    width,
    extraOffset,
}: TooltipComponentProps) {
    const [isTooltipOpen, setIsTooltipOpen] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [offsetX, setOffsetX] = useState(0)
    const [offsetY, setOffsetY] = useState(0)

    const containerRef = useRef<HTMLDivElement>(null)
    const tooltipBoxRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768)
        }
        checkMobile()
        window.addEventListener('resize', checkMobile)
        return () => window.removeEventListener('resize', checkMobile)
    }, [])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (
                isTooltipOpen &&
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setIsTooltipOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('touchstart', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('touchstart', handleClickOutside)
        }
    }, [isTooltipOpen])

    useLayoutEffect(() => {
        if (!isTooltipOpen || !containerRef.current) return

        const tooltipWidth = width ?? 360
        const margin = 10
        const rect = containerRef.current.getBoundingClientRect()

        const scrollLeft = window.scrollX
        const scrollTop = window.scrollY

        const absoluteLeft = rect.left + scrollLeft
        const absoluteTop = rect.top + scrollTop

        let newOffsetX = 0
        let newOffsetY = 0

        if (tooltipPosition === 'top' || tooltipPosition === 'bottom') {
            const baseLeft = absoluteLeft + rect.width / 2 - tooltipWidth / 2
            if (baseLeft < margin) {
                newOffsetX = margin - baseLeft
            } else if (baseLeft + tooltipWidth > window.innerWidth + scrollLeft - margin) {
                newOffsetX = window.innerWidth + scrollLeft - margin - (baseLeft + tooltipWidth)
            }
        } else {
            const tooltipHeight = tooltipBoxRef.current?.offsetHeight || 160
            const baseTop = absoluteTop + rect.height / 2 - tooltipHeight / 2
            if (baseTop < margin) {
                newOffsetY = margin - baseTop
            } else if (baseTop + tooltipHeight > window.innerHeight + scrollTop - margin) {
                newOffsetY = window.innerHeight + scrollTop - margin - (baseTop + tooltipHeight)
            }
        }

        setOffsetX(newOffsetX)
        setOffsetY(newOffsetY)
    }, [isTooltipOpen, tooltipPosition, width])

    const effectivePosition: TooltipProps['position'] = isMobile
        ? 'bottom'
        : tooltipPosition ?? 'top'

    return (
        <div className="pb-2 text-sm lg:mb-0 flex items-center gap-1 z-50">
            {label ?? '라이브 시작 알림 타겟 대상을 선택해주세요.'}
            <TooltipContainer
                ref={containerRef}
                onMouseEnter={!isMobile ? () => setIsTooltipOpen(true) : undefined}
                onClick={isMobile ? () => setIsTooltipOpen(!isTooltipOpen) : undefined}
            >
                <img src={helpSVG} alt="도움말 아이콘" style={{ cursor: 'pointer' }} />
                <TooltipBox
                    ref={tooltipBoxRef}
                    isOpen={isTooltipOpen}
                    position={effectivePosition}
                    offsetX={offsetX}
                    offsetY={offsetY}
                    style={{ padding: isSimple ? '2px' : '16px' }}
                    textAlign={isSimple ? 'left' : undefined}
                    width={width ?? 360}
                    extraOffset={extraOffset}
                >
                    {!isSimple && (
                        <div className="flex justify-end">
                            <CloseButton onClick={() => setIsTooltipOpen(false)}>
                                <img src={closeSVG} alt="닫기" />
                            </CloseButton>
                        </div>
                    )}
                    {tooltipText ?? (
                        <>
                            <p>최근 6개월 참여자</p>
                            <ul>
                                <li>최근 6개월간 라이브에 참여한 고객에게 알림을 발송합니다.</li>
                                <li>본 기능은 단골 3,000명 이상인 경우 이용할 수 있습니다.</li>
                            </ul>
                            <br />
                            <p>최근 3개월 참여자</p>
                            <ul>
                                <li>최근 3개월간 라이브에 참여한 고객에게 알림을 발송합니다.</li>
                                <li>본 기능은 단골 3,000명 이상인 경우 이용할 수 있습니다.</li>
                            </ul>
                        </>
                    )}
                </TooltipBox>
            </TooltipContainer>
        </div>
    )
}
